<template>
  <div class="prompt-editor">
    <div class="card">
      <h2>Prompt Template Editor</h2>
      
      <!-- Template List -->
      <div class="template-section">
        <h3>Existing Templates</h3>
        <div class="template-list">
          <div 
            v-for="template in templates" 
            :key="template._id"
            class="template-item"
            :class="{'active': template.isActive}"
            @click="selectTemplate(template)"
          >
            <div class="template-header">
              <h4>{{ template.name }}</h4>
              <span v-if="template.isActive" class="active-badge">Active</span>
            </div>
            <p class="template-date">Updated: {{ formatDate(template.updatedAt) }}</p>
            <div class="template-actions">
              <button 
                @click.stop="activateTemplate(template._id)" 
                class="btn-small"
                :disabled="template.isActive"
              >
                {{ template.isActive ? 'Active' : 'Set Active' }}
              </button>
              <button @click.stop="deleteTemplate(template._id)" class="btn-small btn-danger">
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Editor Section -->
      <div class="editor-section">
        <h3>{{ editingTemplate ? 'Edit Template' : 'Create New Template' }}</h3>
        
        <div class="form-group">
          <label>Template Name *</label>
          <input 
            v-model="currentTemplate.name" 
            type="text" 
            class="form-control"
            placeholder="e.g., Singapore Banks Credit Analysis v1.0"
          />
        </div>

        <div class="form-group">
          <label>Prompt Template *</label>
          <div class="template-help">
            Use <code>{companyName}</code> as a placeholder for the company name
          </div>
          <textarea 
            v-model="currentTemplate.template" 
            class="form-control template-editor"
            rows="20"
            placeholder="Enter your prompt template here..."
          ></textarea>
        </div>

        <!-- Sample Files Section -->
        <div class="form-group">
          <label>Sample Files (Optional but IMPORTANT!)</label>
          <p class="help-text">
            Upload your Keppel REIT PDF and Excel files here so FinSora knows the exact format you want!
          </p>
          
          <!-- File Upload -->
          <div class="file-upload-section">
            <input 
              ref="fileInput"
              type="file" 
              multiple
              accept=".pdf,.xlsx,.xls"
              @change="handleFileUpload"
              style="display: none"
            />
            <button @click="$refs.fileInput.click()" class="btn btn-secondary">
              📁 Upload Sample Files (PDF & Excel)
            </button>
            <div v-if="selectedFiles.length > 0" class="pending-files">
              <h5>Files to upload:</h5>
              <ul>
                <li v-for="(file, index) in selectedFiles" :key="index">
                  {{ file.name }} ({{ formatFileSize(file.size) }})
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button 
            @click="saveTemplate" 
            class="btn btn-primary"
            :disabled="!currentTemplate.name || !currentTemplate.template"
          >
            Save Template
          </button>
          <button @click="loadDefaultTemplate" class="btn btn-secondary">
            Load Default Template
          </button>
          <button @click="clearEditor" class="btn btn-secondary">
            Clear
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PromptEditor',
  data() {
    return {
      templates: [],
      currentTemplate: {
        name: '',
        template: '',
        sampleFiles: []
      },
      editingTemplate: null,
      selectedFiles: [],
      defaultTemplate: `You are a credit analyst for Singapore banks. Analyze the company {companyName} and generate a comprehensive credit spreading report.

IMPORTANT: Generate output files in the EXACT format shown in the sample files provided.

Data Collection Requirements:
1. Search and retrieve latest financial data from:
   - SGX announcements and filings
   - Annual reports (latest 3-4 years)
   - Quarterly financial statements
   - Company investor relations website

2. Generate two files:
   - PDF Report: Executive summary with credit analysis
   - Excel File: Detailed financials matching the sample format

The Excel file should include:
- Balance Sheet (4 periods)
- Income Statement (4 periods)  
- Cash Flow Statement
- Financial Ratios (all categories from sample)
- Financial Highlights

Format everything EXACTLY like the provided Keppel REIT samples.`
    };
  },
  mounted() {
    this.loadTemplates();
  },
  methods: {
    async loadTemplates() {
      try {
        const response = await axios.get('http://localhost:5001/api/prompts');
        this.templates = response.data;
      } catch (error) {
        console.error('Error loading templates:', error);
      }
    },
    
    selectTemplate(template) {
      this.editingTemplate = template;
      this.currentTemplate = {
        name: template.name,
        template: template.template,
        sampleFiles: template.sampleFiles || []
      };
      this.selectedFiles = [];
    },
    
    async activateTemplate(templateId) {
      try {
        await axios.put(`http://localhost:5001/api/prompts/${templateId}/activate`);
        await this.loadTemplates();
        alert('Template activated successfully!');
      } catch (error) {
        console.error('Error activating template:', error);
        alert('Failed to activate template');
      }
    },
    
    async deleteTemplate(templateId) {
      if (!confirm('Are you sure you want to delete this template?')) return;
      
      try {
        await axios.delete(`http://localhost:5001/api/prompts/${templateId}`);
        await this.loadTemplates();
        if (this.editingTemplate && this.editingTemplate._id === templateId) {
          this.clearEditor();
        }
      } catch (error) {
        console.error('Error deleting template:', error);
        alert('Failed to delete template');
      }
    },
    
    handleFileUpload(event) {
      this.selectedFiles = Array.from(event.target.files);
    },
    
    async saveTemplate() {
      if (!this.currentTemplate.name || !this.currentTemplate.template) {
        alert('Please provide both template name and content');
        return;
      }
      
      try {
        const formData = new FormData();
        formData.append('name', this.currentTemplate.name);
        formData.append('template', this.currentTemplate.template);
        
        if (this.editingTemplate) {
          formData.append('promptId', this.editingTemplate._id);
        }
        
        // Add sample files
        for (const file of this.selectedFiles) {
          formData.append('sampleFiles', file);
        }
        
        const response = await axios.post('http://localhost:5001/api/prompts', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (response.data.success) {
          alert('Template saved successfully! Remember to activate it!');
          await this.loadTemplates();
          this.clearEditor();
        }
      } catch (error) {
        console.error('Error saving template:', error);
        alert('Failed to save template');
      }
    },
    
    loadDefaultTemplate() {
      this.currentTemplate.template = this.defaultTemplate;
    },
    
    clearEditor() {
      this.currentTemplate = {
        name: '',
        template: '',
        sampleFiles: []
      };
      this.editingTemplate = null;
      this.selectedFiles = [];
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-SG');
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.prompt-editor {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.card {
  background: white;
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.template-section {
  margin-bottom: 2rem;
}

.template-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.template-item {
  padding: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.template-item:hover {
  border-color: #667eea;
}

.template-item.active {
  border-color: #28a745;
  background: #f0fff4;
}

.active-badge {
  background: #28a745;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.75rem;
}

.template-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.template-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.editor-section {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 1rem;
}

.template-editor {
  font-family: monospace;
  font-size: 0.9rem;
}

.template-help {
  background: #e7f3ff;
  padding: 0.75rem;
  border-radius: 5px;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.template-help code {
  background: #0066cc;
  color: white;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 5px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
  margin-left: 0.5rem;
}

.btn-small {
  padding: 0.25rem 0.75rem;
  font-size: 0.85rem;
  border: none;
  border-radius: 3px;
  background: #667eea;
  color: white;
  cursor: pointer;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.file-upload-section {
  margin-top: 1rem;
}

.pending-files {
  margin-top: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 5px;
}

.help-text {
  color: #666;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}
</style>
